import React, {
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { setOpenContextMenuId } from '../../stores/actions/user-interface';
import { GreenroomState } from '../../stores/types/types';
import GraphicMenuItem from '../context-menu/graphic-menu-item';
import Portal from './portal';
import useClickAwayListener from '../../hooks/use-click-away-listener';

interface RightClickMenuProps {
	clickCoordinates?: [number, number]; // [pageX, pageY]; undefined results in [0, 0]
	contextMenuId: string; // make it unique across the app to the clicked item
	items: MenuItem[];
}

export interface MenuItem {
	checkboxValue?: boolean; // if provided, replaces icon with a checkbox
	color?: string; // text and icon color
	disabled?: boolean;
	handleClick?: () => any;
	hidden?: boolean; // excludes the item from menu if true
	iconName?: string;
	iconSize?: number;
	CustomSubMenu?: React.ReactNode; // if provided, replaces submenu with a custom component
	subMenuItems?: MenuItem[]; // hover over parent to reveal these
	text?: string;
	multipleItems?: boolean
	dontClose?: boolean
}

export default function RightClickMenu(props: RightClickMenuProps) {
	const { clickCoordinates, contextMenuId, items } = props;

	const [hoveredItemKey, setHoveredItemKey] = useState<string | null>(null);
	const [hideMenu, setHideMenu] = useState<boolean>(true);
	const menuRef = useRef<HTMLInputElement>(null);
	const subMenuRef = useRef<HTMLInputElement>(null);
	const openContextMenuId = useSelector((s: GreenroomState) => s.UserInterfaceReducer.openContextMenuId);
	const dispatch = useDispatch();

	const pageX = clickCoordinates?.[0] || 0;
	const pageY = clickCoordinates?.[1] || 0;

	const onClose = () => {
		dispatch(setOpenContextMenuId(null));
	};

	useClickAwayListener({
		ref: menuRef,
		onClickAway: () => null,
		// don't use onMouseDownClickAway or it will flash between pickers
		// because it will take a moment for the next picker to render
		onMousedownClickAway: (e) => {
			// if the target is a child of the submenu, don't close
			if (subMenuRef.current?.contains(e.target as Node)) return;
			onClose()
		},
		onEscape: () => onClose(),
		open: openContextMenuId === contextMenuId && !hideMenu,
	});

	// Allows the menu to be imperceptibly repositioned after its dimensions are computed
	useEffect(() => {
		if (menuRef.current && openContextMenuId === contextMenuId) setHideMenu(false);
		if (openContextMenuId !== contextMenuId) {
			setHoveredItemKey(null);
			setHideMenu(true);
		}
	}, [items, menuRef, openContextMenuId, contextMenuId]);

	// This is used to ensure there's enough room to open the submenu
	const submenuCount = useMemo(
		// Can count recursively to get space for more submenus,
		// but as-is the main menu will get pushed away from the click
		() => (items.some((item) => !!item.subMenuItems?.length) ? 2 : 1),
		[items],
	);

	// Only one context menu can be open at a time
	if (contextMenuId !== openContextMenuId || contextMenuId.includes('undefined')) return null;

	const gap = 5;
	const menuHeight = menuRef.current?.offsetHeight || 0;
	const menuWidth = (menuRef.current?.offsetWidth || 0) * submenuCount;
	const positionX = (
		pageX + menuWidth > window.innerWidth ? window.innerWidth - menuWidth : pageX
	) + gap;
	const positionY = (pageY - menuHeight < 0 ? menuHeight : pageY) + 20;

	return (
		<>
			<Portal targetId='right-click-portal'>
				<div
					data-testid="right-click-context-menu"
					id="feed-media-context-menu-custom-content-portal"
					className="right-click-menu"
					style={{
						top: `${positionY - 41}px`,
						left: `${positionX + menuWidth / submenuCount + gap}px`,
					}}
					ref={subMenuRef}
				/>
				<div className="graphic-menu-container" style={{ top: `${positionY}px`, left: `${positionX}px` }}>
					<div className="graphic-menu" ref={menuRef} style={{ display: hideMenu ? 'none' : 'flex' }}>
						{items
							.filter((item) => !item.hidden)
							.map((item, i) => {
								const key = `rt-click-menu-item-${i}`;
								return (
									<GraphicMenuItem
										id={key}
										item={item}
										setHoveredItemKey={setHoveredItemKey}
										hoveredItemKey={hoveredItemKey}
										key={key}
										multipleItems={items.length > 1}
										subMenuPortalTargetId="feed-media-context-menu-custom-content-portal"
									/>
								);
							})}
					</div>
				</div>
			</Portal>
		</>
	);
}
